import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { getEnv } from "../../shared/lib/env";
import { PageScaffold } from "../../shared/ui/PageScaffold";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";

export function EntryPage() {
  const env = getEnv();
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession, lineLoginWithMockToken } = useSessionBootstrap();

  useEffect(() => {
    void bootstrapSession(tenantSlug);
  }, [tenantSlug]);

  useEffect(() => {
    if (status === "authenticated" && session) {
      navigate(session.entryPath, { replace: true });
    }
  }, [status, session, navigate]);

  if (status === "authenticated" && session) {
    return <Navigate to={session.entryPath} replace />;
  }

  return (
    <PageScaffold
      title="Entry"
      description="LINE認証後の分岐画面です。mock session bootstrap で preMember / family / operator の振り分けまで実装しています。"
    >
      {status === "loading" ? <p>セッションを確認しています。</p> : null}
      {status === "unauthenticated" ? <p>未ログインです。現在は mock LINE login で session bootstrap を確認できます。</p> : null}
      {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}

      {env.enableMock ? (
        <div style={{ display: "grid", gap: 12 }}>
          <button type="button" onClick={() => void lineLoginWithMockToken(tenantSlug, "mock-pre-member-token")}>
            Mock Login: preMember
          </button>
          <button type="button" onClick={() => void lineLoginWithMockToken(tenantSlug, "mock-parent-token")}>
            Mock Login: family
          </button>
          <button type="button" onClick={() => void lineLoginWithMockToken(tenantSlug, "mock-operator-token")}>
            Mock Login: operator
          </button>
          <button type="button" onClick={() => void lineLoginWithMockToken(tenantSlug, "mock-teacher-token")}>
            Mock Login: teacher
          </button>
        </div>
      ) : (
        <p>real LINE login は未実装です。mock login を有効にするには `VITE_ENABLE_MOCK=true` を使用してください。</p>
      )}
    </PageScaffold>
  );
}

export function LinkStudentPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [inviteToken, setInviteToken] = useState("demo-family-token");
  const [manualCode, setManualCode] = useState("FAMILY-DEMO");

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  return (
    <PageScaffold
      title="Link Student"
      description="招待URL / 招待コードの確認画面と、申請導線の入口です。現在は in-memory guardian invite flow を stub data で接続しています。"
      links={[
        { to: "/t/:tenantSlug/entry", label: "Entryへ戻る" },
        { to: "/t/:tenantSlug/link-student/request", label: "申請する" }
      ]}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            session が見つかりません。まず <Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!inviteToken.trim()) {
              return;
            }

            navigate(
              `/t/${tenantSlug}/link-student/confirm?inviteToken=${encodeURIComponent(inviteToken.trim())}`
            );
          }}
          style={{ display: "grid", gap: 8 }}
        >
          <label style={{ display: "grid", gap: 4 }}>
            <span>招待URL token</span>
            <input
              value={inviteToken}
              onChange={(event) => setInviteToken(event.target.value)}
              placeholder="demo-family-token"
            />
          </label>
          <button type="submit">招待URLを確認する</button>
        </form>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!manualCode.trim()) {
              return;
            }

            navigate(
              `/t/${tenantSlug}/link-student/confirm?manualCode=${encodeURIComponent(manualCode.trim())}`
            );
          }}
          style={{ display: "grid", gap: 8 }}
        >
          <label style={{ display: "grid", gap: 4 }}>
            <span>招待コード</span>
            <input
              value={manualCode}
              onChange={(event) => setManualCode(event.target.value)}
              placeholder="FAMILY-DEMO"
            />
          </label>
          <button type="submit">招待コードを確認する</button>
        </form>

        <div style={{ fontSize: 14, opacity: 0.8 }}>
          <p style={{ margin: 0 }}>stub demo values</p>
          <p style={{ margin: "4px 0 0" }}>family token: `demo-family-token` / family code: `FAMILY-DEMO`</p>
          <p style={{ margin: "4px 0 0" }}>student token: `demo-student-token` / student code: `ANNA-DEMO`</p>
        </div>
      </div>
    </PageScaffold>
  );
}

export function NotFoundPage() {
  return (
    <PageScaffold title="Not Found" description="指定されたページは見つかりません。">
      <p>ルーティングを確認してください。</p>
    </PageScaffold>
  );
}

export { ContactSettingsPage } from "./ContactSettingsPage";
export { LinkStudentConfirmPage } from "./LinkStudentConfirmPage";
export { LinkStudentRequestPage } from "./LinkStudentRequestPage";
